<script setup>
import { computed } from "vue";

const props = defineProps({
  size: {
    type: String,
    default: "medium",
    validator: (value) => ["extra-small", "small", "medium", "large"].includes(value)
  },
  pressed: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  iconOnly: {
    type: Boolean,
    default: false
  },
  active: {
    type: Boolean,
    default: false
  }
});

const buttonClasses = computed(() => [
  `button-accent-secondary--${props.size}`,
  {
    "button-accent-secondary--icon-only": props.iconOnly,
    "button-accent-secondary--pressed": props.pressed,
    "button-accent-secondary--active": props.active
  }
]);
</script>

<template>
  <button
    class="button-accent-secondary"
    :disabled="props.disabled"
    :class="buttonClasses"
    :aria-pressed="props.pressed ? 'true' : 'false'"
    type="button"
  >
    <slot />
  </button>
</template>

<style scoped lang="scss">

.button-accent-secondary {
  // Сброс дефолтных стилей.
  border: none;
  outline: none;
  background: none;
  margin: 0;
  font: inherit;

  // Стили кнопки.
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
  padding: 6px 15px;
  background: var(--button-secondary-accent-default);
  color: var(--button-secondary-accent-default-text);
  border-radius: 50px;
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;

  box-shadow: 1px 2px 10px 0 var(--gray-gray50);

  cursor: pointer;
  user-select: none;

  // Если кнопка содержит только иконку - убираем отступы, задаем ширину равной высоте.
  &--icon-only {
    padding: 0;
    width: var(--button-height);
  }

  // Состояния

  &:hover:not(:disabled):not(&--pressed):not(&--active) {
    background: var(--button-secondary-accent-hover);
    color: var(--button-secondary-accent-hover-text);
  }

  &:focus-visible {
    outline: 2px solid var(--button-secondary-accent-bd-focused);
    outline-offset: 2px;
  }

  &:disabled {
    pointer-events: none;
    color: var(--text-text-disabled);
  }

  &--pressed {
    pointer-events: none;
    color: var(--button-secondary-accent-pressed-text);
    background: var(--button-secondary-accent-pressed);
  }

  &--active {
    color: var(--button-primary-accent-text);
    background: var(--blue-blue100);
  }

  // Размеры
  &--extra-small {
    --button-height: 24px;
    height: var(--button-height);
    min-height: var(--button-height);
  }

  &--small {
    --button-height: 30px;
    height: var(--button-height);
    min-height: var(--button-height);
  }

  &--medium {
    --button-height: 36px;
    height: var(--button-height);
    min-height: var(--button-height);
  }

  &--large {
    --button-height: 42px;
    height: var(--button-height);
    min-height: var(--button-height);
  }
}

</style>